import React, { useState, useEffect, useCallback, useMemo } from 'react';
import confetti from 'canvas-confetti';
import { functions2D } from '../../data/functions2d';
import { functions3D } from '../../data/functions3d';
import { MathItem, QuizChallenge, QuizOption, UserStats, Dimension } from '../../types/math';
import { Plot2D } from '../renderers/Plot2D';
import { Plot3D } from '../renderers/Plot3D';
import { MathFormula } from './MathFormula';
import { IntuitionDrawer } from './IntuitionDrawer';
import { ScoreStreak } from './ScoreStreak';
import { sound } from '../../services/sound';
import { Sparkles, ArrowRight, HelpCircle, Check, X, Shuffle, Box, Layers } from 'lucide-react';

export const CardGame: React.FC = () => {
  const [filterDimension, setFilterDimension] = useState<Dimension | 'all'>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [challenge, setChallenge] = useState<QuizChallenge | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [isMuted, setIsMuted] = useState(sound.getMuted());

  const [stats, setStats] = useState<UserStats>(() => {
    const saved = localStorage.getItem('math_user_stats');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // ignore
      }
    }
    return {
      score: 0,
      streak: 0,
      maxStreak: 0,
      totalAnswered: 0,
      correctAnswered: 0
    };
  });

  // Save stats to localStorage
  useEffect(() => {
    localStorage.setItem('math_user_stats', JSON.stringify(stats));
  }, [stats]);

  // Pool of available items based on filter
  const pool = useMemo(() => {
    let items: MathItem[] = [];
    if (filterDimension === 'all' || filterDimension === '2d') {
      items = items.concat(functions2D);
    }
    if (filterDimension === 'all' || filterDimension === '3d') {
      items = items.concat(functions3D);
    }
    if (selectedCategory !== 'all') {
      items = items.filter((it) => it.category === selectedCategory);
    }
    return items;
  }, [filterDimension, selectedCategory]);

  // Generate next challenge
  const nextChallenge = useCallback(() => {
    if (pool.length === 0) return;

    // Pick random item from pool (different from current if pool > 1)
    const available = challenge && pool.length > 1
      ? pool.filter((it) => it.id !== challenge.item.id)
      : pool;

    const randomIndex = Math.floor(Math.random() * available.length);
    const chosenItem = available[randomIndex];

    // Build options: 1 correct + up to 3 distractors
    const options: QuizOption[] = [
      {
        id: 'correct',
        formulaLatex: chosenItem.formulaLatex,
        isCorrect: true
      },
      ...chosenItem.distractors.slice(0, 3).map((d, idx) => ({
        id: `distractor_${idx}`,
        formulaLatex: d.formulaLatex,
        isCorrect: false,
        explanation: d.explanation
      }))
    ];

    // Shuffle options
    for (let i = options.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [options[i], options[j]] = [options[j], options[i]];
    }

    setChallenge({
      item: chosenItem,
      options
    });
    setSelectedOptionId(null);
    setIsAnswered(false);
    setShowHint(false);
  }, [pool, challenge]);

  // Initial load
  useEffect(() => {
    nextChallenge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDimension, selectedCategory]);

  // Handle option selection
  const handleSelectOption = (option: QuizOption) => {
    if (isAnswered) return;

    setSelectedOptionId(option.id);
    setIsAnswered(true);

    const isCorrect = option.isCorrect;

    if (isCorrect) {
      sound.playCorrect();
      const newStreak = stats.streak + 1;
      const newMax = Math.max(newStreak, stats.maxStreak);

      setStats((prev) => ({
        ...prev,
        score: prev.score + 100 * Math.min(newStreak, 5),
        streak: newStreak,
        maxStreak: newMax,
        totalAnswered: prev.totalAnswered + 1,
        correctAnswered: prev.correctAnswered + 1
      }));

      // Confetti celebrations at milestone streaks
      if (newStreak % 5 === 0) {
        sound.playStreak();
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { y: 0.6 }
        });
      }
    } else {
      sound.playIncorrect();
      setStats((prev) => ({
        ...prev,
        streak: 0,
        totalAnswered: prev.totalAnswered + 1
      }));
    }
  };

  // Keyboard navigation: 1-4 for options, Space/Enter for Next
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in an input
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) return;

      if (!isAnswered && challenge) {
        if (['1', '2', '3', '4'].includes(e.key)) {
          const index = parseInt(e.key, 10) - 1;
          if (challenge.options[index]) {
            handleSelectOption(challenge.options[index]);
          }
        }
      } else if (isAnswered) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          nextChallenge();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAnswered, challenge, nextChallenge]);

  const handleToggleSound = () => {
    const muted = sound.toggleMute();
    setIsMuted(muted);
  };

  if (!challenge) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400">
        Loading challenge cards...
      </div>
    );
  }

  const selectedOption = challenge.options.find((o) => o.id === selectedOptionId);
  const isCorrect = selectedOption?.isCorrect ?? null;

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6">
      {/* Top Bar: Dimension filters & Stats */}
      <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
        {/* Dimension & Category Selector */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => {
              setFilterDimension('all');
              setSelectedCategory('all');
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-mono font-medium transition flex items-center gap-1.5 ${
              filterDimension === 'all'
                ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40'
                : 'bg-slate-900/60 text-slate-400 border border-slate-800 hover:text-slate-200'
            }`}
          >
            <Shuffle className="w-3.5 h-3.5" /> All Decks ({functions2D.length + functions3D.length})
          </button>
          <button
            onClick={() => {
              setFilterDimension('2d');
              setSelectedCategory('all');
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-mono font-medium transition flex items-center gap-1.5 ${
              filterDimension === '2d'
                ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40'
                : 'bg-slate-900/60 text-slate-400 border border-slate-800 hover:text-slate-200'
            }`}
          >
            <Layers className="w-3.5 h-3.5 text-teal-400" /> 2D Curves ({functions2D.length})
          </button>
          <button
            onClick={() => {
              setFilterDimension('3d');
              setSelectedCategory('all');
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-mono font-medium transition flex items-center gap-1.5 ${
              filterDimension === '3d'
                ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40'
                : 'bg-slate-900/60 text-slate-400 border border-slate-800 hover:text-slate-200'
            }`}
          >
            <Box className="w-3.5 h-3.5 text-purple-400" /> 3D Surfaces ({functions3D.length})
          </button>
        </div>

        {/* Live Score and Streak Counter */}
        <ScoreStreak stats={stats} isMuted={isMuted} onToggleSound={handleToggleSound} />
      </div>

      {/* Main Flashcard Stage */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Graph Display Viewport */}
        <div className="lg:col-span-7 flex flex-col gap-2">
          <div className="w-full aspect-[4/3] sm:aspect-[16/11] relative">
            {challenge.item.dimension === '2d' ? (
              <Plot2D item={challenge.item} showAsymptotes={true} interactive={true} />
            ) : (
              <Plot3D item={challenge.item} interactive={true} />
            )}
          </div>

          <div className="flex items-center justify-between text-xs text-slate-400 px-1 font-mono">
            <span className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              What governing equation produced this geometry?
            </span>
            <button
              onClick={() => setShowHint(!showHint)}
              className="text-slate-400 hover:text-amber-300 transition flex items-center gap-1"
            >
              <HelpCircle className="w-3.5 h-3.5" />
              {showHint ? 'Hide Clue' : 'Hint'}
            </button>
          </div>

          {/* Hint callout if requested */}
          {showHint && !isAnswered && (
            <div className="bg-amber-950/30 border border-amber-800/50 rounded-lg p-3 text-xs text-amber-200">
              <span className="font-semibold text-amber-300">Intuition Clue:</span>{' '}
              {challenge.item.intuition.keySignatures[0]}
            </div>
          )}
        </div>

        {/* Multiple-Choice Equation Options & Controls */}
        <div className="lg:col-span-5 flex flex-col gap-3">
          <div className="text-xs font-mono text-slate-400 uppercase tracking-wider flex items-center justify-between">
            <span>Select Matching Equation:</span>
            <span className="text-slate-500 font-normal">Press keys [1-4]</span>
          </div>

          {challenge.options.map((option, idx) => {
            const isSelected = selectedOptionId === option.id;
            const isOptionCorrect = option.isCorrect;

            let buttonStyle = 'bg-slate-900/90 border-slate-800 text-slate-200 hover:border-slate-600 hover:bg-slate-850';

            if (isAnswered) {
              if (isOptionCorrect) {
                buttonStyle = 'bg-teal-950/60 border-teal-500 text-teal-200 shadow-lg shadow-teal-500/10';
              } else if (isSelected && !isOptionCorrect) {
                buttonStyle = 'bg-rose-950/60 border-rose-500 text-rose-200 shadow-lg shadow-rose-500/10';
              } else {
                buttonStyle = 'opacity-40 border-slate-800 text-slate-500';
              }
            }

            return (
              <button
                key={option.id}
                onClick={() => handleSelectOption(option)}
                disabled={isAnswered}
                className={`relative w-full p-4 rounded-xl border text-left transition-all duration-200 flex items-center justify-between group ${buttonStyle}`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-md bg-slate-800/80 border border-slate-700/60 flex items-center justify-center text-xs font-mono text-slate-400 group-hover:text-white transition">
                    {idx + 1}
                  </span>
                  <div className="text-base font-medium">
                    <MathFormula latex={option.formulaLatex} displayMode={false} />
                  </div>
                </div>

                {isAnswered && (
                  <div>
                    {isOptionCorrect && (
                      <span className="flex items-center gap-1 text-xs font-mono text-teal-400 font-semibold">
                        <Check className="w-4 h-4" /> Correct
                      </span>
                    )}
                    {isSelected && !isOptionCorrect && (
                      <span className="flex items-center gap-1 text-xs font-mono text-rose-400 font-semibold">
                        <X className="w-4 h-4" /> Trap
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}

          {/* Next Card Button (always visible once answered) */}
          {isAnswered && (
            <button
              onClick={nextChallenge}
              className="w-full mt-2 py-3.5 px-6 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-400 hover:to-emerald-500 text-slate-950 font-semibold flex items-center justify-center gap-2 shadow-lg shadow-teal-500/20 transition-all transform active:scale-98"
            >
              <span>Next Challenge</span>
              <ArrowRight className="w-4 h-4" />
              <span className="text-xs font-mono opacity-70 ml-1 font-normal">(Press Space)</span>
            </button>
          )}
        </div>
      </div>

      {/* Intuition Diagnostic Breakdown (Drawer) */}
      {isAnswered && (
        <IntuitionDrawer
          item={challenge.item}
          selectedOptionId={selectedOptionId}
          isCorrect={isCorrect}
        />
      )}
    </div>
  );
};
